import React, { useRef, useState, useEffect } from 'react';
import { IonButton, IonIcon } from '@ionic/react';
import { volumeHighOutline, stopCircleOutline } from 'ionicons/icons';
import { Polly } from 'aws-sdk';
import { segmentText } from './languageUtils';
import { LoadingSpinner } from '../common/LoadingSpinner';

const polly = new Polly({
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  region: 'us-east-1'
});

interface AudioPlaybackProps {
  content: string;
  messageIndex: number;
  teacherId: string;
}

export const AudioPlayback: React.FC<AudioPlaybackProps> = ({
  content,
  messageIndex,
  teacherId
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPlayingMessage, setCurrentPlayingMessage] = useState<number | null>(null);
  const [playbackError, setPlaybackError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handlePlaybackError = (error: any) => {
    console.error('Playback error:', error);
    setPlaybackError('Error playing audio. Please try again.');
    setIsPlaying(false);
    setIsLoading(false);
    setCurrentPlayingMessage(null);
    if (audioRef.current) {
      audioRef.current = null;
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      setCurrentPlayingMessage(null);
      audioRef.current = null;
    }
  };

  const playAudio = async () => {
    // Prevent starting new playback if already processing
    if (isPlaying || isLoading) {
      return;
    }

    // If there's already audio playing, stop it
    if (audioRef.current) {
      stopAudio();
      return;
    }

    setIsLoading(true);
    setPlaybackError(null);
    setCurrentPlayingMessage(messageIndex);

    try {
      const segments = segmentText(content);
      let currentSegmentIndex = 0;

      const playNextSegment = async () => {
        if (currentSegmentIndex >= segments.length) {
          setIsPlaying(false);
          setIsLoading(false);
          setCurrentPlayingMessage(null);
          audioRef.current = null;
          return;
        }

        const segment = segments[currentSegmentIndex];
        const params = {
          Text: segment.text,
          OutputFormat: 'mp3',
          VoiceId: getVoiceId(segment.language),
          LanguageCode: getLanguageCode(segment.language),
        };

        try {
          const data = await polly.synthesizeSpeech(params).promise();
          
          // Handle both ArrayBuffer and Uint8Array responses
          let audioData: Uint8Array;
          if (data.AudioStream instanceof ArrayBuffer) {
            audioData = new Uint8Array(data.AudioStream);
          } else if (data.AudioStream instanceof Uint8Array) {
            audioData = data.AudioStream;
          } else {
            throw new Error('Invalid AudioStream format');
          }

          const blob = new Blob([audioData], { type: 'audio/mpeg' });
          const url = URL.createObjectURL(blob);

          // Create and configure audio element
          const audio = new Audio();
          audio.src = url;
          audioRef.current = audio;

          // Set up event handlers before loading
          audio.onerror = (e) => {
            console.error('Audio playback error:', e);
            URL.revokeObjectURL(url);
            handlePlaybackError(e);
          };

          audio.onended = () => {
            URL.revokeObjectURL(url);
            audioRef.current = null;
            currentSegmentIndex++;
            playNextSegment();
          };

          // Load and play
          await new Promise((resolve, reject) => {
            audio.oncanplaythrough = resolve;
            audio.onerror = reject;
            audio.load();
          });

          setIsLoading(false);
          setIsPlaying(true);

          try {
            await audio.play();
          } catch (error) {
            if (error instanceof Error && error.name === 'NotAllowedError') {
              // Retry once with user interaction
              const playPromise = audio.play();
              if (playPromise !== undefined) {
                await playPromise;
              }
            } else {
              throw error;
            }
          }
        } catch (error) {
          handlePlaybackError(error);
        }
      };

      await playNextSegment();
    } catch (error) {
      handlePlaybackError(error);
    }
  };

  const getVoiceId = (language: 'ru' | 'fr' | 'en'): string => {
    switch (language) {
      case 'ru':
        return teacherId === 'anton' ? 'Maxim' : 'Tatyana';
      case 'fr':
        return teacherId === 'anton' ? 'Mathieu' : 'Lea';
      default: // 'en'
        return teacherId === 'anton' ? 'Matthew' : 'Joanna';
    }
  };

  const getLanguageCode = (language: 'ru' | 'fr' | 'en'): string => {
    switch (language) {
      case 'ru':
        return 'ru-RU';
      case 'fr':
        return 'fr-FR';
      default: // 'en'
        return 'en-US';
    }
  };

  // Clean up on unmount
  React.useEffect(() => {
    return () => {
      if (audioRef.current) {
        stopAudio();
      }
    };
  }, []);

  return (
    <div className="relative">
      <IonButton
        fill="clear"
        onClick={playAudio}
        className="p-0 h-8 mt-1"
        disabled={isLoading}
      >
        {isLoading ? (
          <LoadingSpinner size="small" color="#31D526" />
        ) : (
          <IonIcon
            icon={currentPlayingMessage === messageIndex ? stopCircleOutline : volumeHighOutline}
            className={`text-[#31D526] w-6 h-6 ${currentPlayingMessage === messageIndex ? 'animate-pulse' : ''}`}
          />
        )}
      </IonButton>
      {playbackError && (
        <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-red-500 text-white text-sm rounded-lg px-3 py-2 whitespace-nowrap z-10 min-w-max shadow-lg">
          {playbackError}
          <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-red-500 rotate-45" />
        </div>
      )}
    </div>
  );
};